// Admin -> Feedback: what users sent with "Send feedback" (newest first), by status (new / read / done) and module;
// mark each one and keep a note.
import { Badge, Button, Group, SegmentedControl, Select, Stack, Table, Text, Textarea } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ErrorBox, notifyError } from "../../components/Errors";
import { formatDate, get, patch } from "../../lib/api";

export interface FeedbackItem {
  id: number; product: string; kind: string; rating: number | null; message: string; context: Record<string, string>;
  status: "new" | "read" | "done"; created_at: string | null; note: string; updated_at: string | null;
  user: { id: number; email: string; name: string } | null;
}
const STATUS_COLOUR = { new: "orange", read: "blue", done: "teal" } as const;

export function FeedbackTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("new");
  const [product, setProduct] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["admin-feedback", status, product],
    queryFn: () => get<{ items: FeedbackItem[]; counts: Record<string, number> }>(
      `/admin/feedback?${new URLSearchParams({ ...(status !== "all" ? { status } : {}), ...(product ? { product } : {}) })}`) });
  const mark = useMutation({
    mutationFn: ({ id, ...b }: { id: number; status?: string; note?: string }) => patch(`/admin/feedback/${id}`, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-feedback"] }), onError: (e) => notifyError(e) });
  const c = q.data?.counts ?? {};
  return (
    <Stack gap="sm" data-testid="admin-feedback">
      <Group gap="sm">
        <SegmentedControl size="xs" value={status} onChange={setStatus} data={[
          { value: "new", label: `New (${c.new ?? 0})` }, { value: "read", label: `Read (${c.read ?? 0})` },
          { value: "done", label: `Done (${c.done ?? 0})` }, { value: "all", label: "All" }]} />
        <Select size="xs" w={160} placeholder="All modules" clearable value={product} onChange={setProduct}
          data={[{ value: "dft", label: "DFT" }, { value: "skfitter", label: "Tight Binding" }, { value: "negf", label: "NEGF Transport" },
            { value: "transport", label: "Spin-Valley Qubits" }]} />
      </Group>
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 && <Text size="sm" c="dimmed">Nothing here.</Text>}
      {q.data && q.data.items.length > 0 && (
        <Table verticalSpacing={6} fz="sm" striped>
          <Table.Thead><Table.Tr><Table.Th>From</Table.Th><Table.Th>Message</Table.Th><Table.Th>Note</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {q.data.items.map((f) => <Row key={f.id} f={f} onMark={(b) => mark.mutate({ id: f.id, ...b })} />)}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function Row({ f, onMark }: { f: FeedbackItem; onMark: (b: { status?: string; note?: string }) => void }) {
  const [note, setNote] = useState(f.note);
  return (
    <Table.Tr>
      <Table.Td w={220}>
        <Text size="sm">{f.user?.name || f.user?.email || "(deleted user)"}</Text>
        <Text size="xs" c="dimmed">{f.user?.email}</Text>
        <Text size="xs" c="dimmed">{f.created_at ? formatDate(f.created_at, true) : ""}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4} mb={4}><Badge size="xs" color={STATUS_COLOUR[f.status]}>{f.status}</Badge>
          <Badge size="xs" variant="light">{f.product || "-"}</Badge><Badge size="xs" variant="outline">{f.kind}</Badge>
          {f.rating ? <Text size="xs">{"★".repeat(f.rating)}{"☆".repeat(5 - f.rating)}</Text> : null}</Group>
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{f.message}</Text>
        {Object.keys(f.context).length > 0 && <Text size="xs" c="dimmed">{Object.entries(f.context).map(([k, v]) => `${k}: ${v}`).join(" · ")}</Text>}
      </Table.Td>
      <Table.Td w={240}>
        <Textarea size="xs" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} aria-label={`note ${f.id}`}
          onBlur={() => note !== f.note && onMark({ note })} />
      </Table.Td>
      <Table.Td w={150}>
        <Stack gap={4}>
          {f.status !== "read" && <Button size="compact-xs" variant="light" onClick={() => onMark({ status: "read" })}>Mark read</Button>}
          {f.status !== "done" && <Button size="compact-xs" variant="light" color="teal" onClick={() => onMark({ status: "done" })}
            aria-label={`done ${f.id}`}>Done</Button>}
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}
